import { useCallback, useRef, useState } from "react";
import { Sentry } from "@/lib/sentry";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatar } from "@/hooks/useAvatar";
import { invalidateAvatarCache } from "@/hooks/useAvatar";
import SelfieConsentGate from "./SelfieConsentGate";
import MetaPersonCreator from "./MetaPersonCreator";
import {
  fetchCreatorToken,
  importGeneratedAvatar,
  readPendingAvatar,
  clearPendingAvatar,
  type MetaPersonExportResult,
  type MetaPersonGender,
  type MetaPersonPending,
} from "@/lib/metaperson/metapersonCreator";

interface MetaPersonAvatarFlowProps {
  avatarCodeToEdit?: string;
  onComplete: () => void;
  onCancel: () => void;
}

type Phase = "resume" | "consent" | "loadingToken" | "creator" | "importing" | "done" | "error";

function toMetaPersonGender(sex: string | null): MetaPersonGender {
  if (sex === "male") return "male";
  if (sex === "female") return "female";
  return ""; // unknown/other → let the Creator ask.
}

export default function MetaPersonAvatarFlow({
  avatarCodeToEdit,
  onComplete,
  onCancel,
}: MetaPersonAvatarFlowProps) {
  const { t } = useLanguage();
  const { sex } = useAvatar();
  const gender = toMetaPersonGender(sex);

  // Reload resilience: a generation that landed (model_generated) but wasn't
  // imported yet. Only honoured for the fresh-avatar flow — when the parent
  // passes avatarCodeToEdit it is already driving a specific avatar, so we
  // never second-guess it with a resume prompt.
  const [pending, setPending] = useState<MetaPersonPending | null>(() =>
    avatarCodeToEdit ? null : readPendingAvatar(),
  );
  // Which avatar the Creator should reopen via show_avatar. Defaults to the
  // parent's prop; "resume" swaps in the pending code.
  const [editCode, setEditCode] = useState<string | undefined>(avatarCodeToEdit);

  const [phase, setPhase] = useState<Phase>(() => (pending ? "resume" : "consent"));
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Last export — kept so a failed import can be retried without redoing the
  // whole Creator session.
  const lastExportRef = useRef<MetaPersonExportResult | null>(null);

  const loadToken = useCallback(async () => {
    setPhase("loadingToken");
    setError(null);
    try {
      const t0 = await fetchCreatorToken();
      setToken(t0);
      setPhase("creator");
    } catch (err) {
      console.warn("[MetaPersonAvatarFlow] token fetch failed:", err);
      try {
        Sentry.captureException(err, { tags: { feature: "metaperson_token_failed" } });
      } catch { /* Sentry not initialized in dev */ }
      setError(t("metaperson.error.token"));
      setPhase("error");
    }
  }, [t]);

  const runImport = useCallback(
    async (result: MetaPersonExportResult) => {
      lastExportRef.current = result;
      setPhase("importing");
      setError(null);
      try {
        await importGeneratedAvatar(result);
        clearPendingAvatar(); // imported successfully — nothing left to resume
        invalidateAvatarCache();
        setPhase("done");
        onComplete();
      } catch (err) {
        // importGeneratedAvatar already retried 3x with a fresh session and
        // captured the final failure — this is the UI-side trail only. The
        // error phase keeps lastExportRef, so "try again" re-runs JUST the
        // import (the Creator session isn't lost) — failure ≠ option removed.
        console.error("[MetaPersonAvatarFlow] import failed:", err);
        setError(t("metaperson.error.import"));
        setPhase("error");
      }
    },
    [onComplete, t],
  );

  const handleExported = useCallback(
    (result: MetaPersonExportResult) => {
      runImport(result);
    },
    [runImport],
  );

  const handleCreatorError = useCallback(() => {
    setError(t("metaperson.error.creator"));
    setPhase("error");
  }, [t]);

  const handleRetry = useCallback(() => {
    // If we already have an export, retry just the import; otherwise restart
    // from the token step.
    if (lastExportRef.current) {
      runImport(lastExportRef.current);
    } else {
      loadToken();
    }
  }, [runImport, loadToken]);

  // Resume: reopen the Creator on the pending avatarCode (show_avatar path) —
  // no consent gate / selfie again, since the user already generated it.
  const handleResume = useCallback(() => {
    if (pending) setEditCode(pending.avatarCode);
    loadToken();
  }, [pending, loadToken]);

  // Discard the pending generation and fall back to the normal fresh flow.
  const handleStartOver = useCallback(() => {
    clearPendingAvatar();
    setPending(null);
    setEditCode(undefined);
    setPhase("consent");
  }, []);

  // Any cancel abandons the pending generation too.
  const handleCancel = useCallback(() => {
    clearPendingAvatar();
    onCancel();
  }, [onCancel]);

  if (phase === "resume" && pending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 px-6 text-center">
        <p className="text-[14px] font-display text-foreground">
          {t("metaperson.resume.title")}
        </p>
        <p className="text-[13px] font-display text-muted-foreground -mt-2">
          {t("metaperson.resume.body")}
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={handleResume}
            className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]"
          >
            {t("metaperson.resume.resumeCta")}
          </button>
          <button
            type="button"
            onClick={handleStartOver}
            className="w-full h-11 rounded-xl text-sm font-display text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("metaperson.resume.startOverCta")}
          </button>
        </div>
      </div>
    );
  }

  if (phase === "consent") {
    return <SelfieConsentGate onConsented={loadToken} onCancel={handleCancel} />;
  }

  if (phase === "creator" && token) {
    return (
      <MetaPersonCreator
        accessToken={token}
        gender={gender}
        avatarCodeToEdit={editCode}
        onExported={handleExported}
        onError={handleCreatorError}
        onCancel={handleCancel}
      />
    );
  }

  if (phase === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 px-6 text-center">
        <p className="text-[14px] font-display text-foreground">
          {error || t("common.error")}
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={handleRetry}
            className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98]"
          >
            {t("common.tryAgain")}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="w-full h-11 rounded-xl text-sm font-display text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    );
  }

  // loadingToken / importing / done → spinner with contextual copy.
  const label =
    phase === "importing"
      ? t("metaperson.importing")
      : t("metaperson.loadingToken");
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
      <div className="w-7 h-7 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-[13px] font-display text-muted-foreground">{label}</p>
    </div>
  );
}
